import React, { useState, useEffect, useRef, useMemo } from "react";
import { useMercuryContext } from "../../context";
import axios from "axios";
import { Helmet } from "react-helmet";

import { Form, FormInstance } from "antd";

import AddHelixTemplate from "manage/helix-templates/AddHelixTemplate";
import EditHelixTemplate from "manage/helix-templates/EditHelixTemplate";
import DeleteHelixTemplate from "manage/helix-templates/DeleteHelixTemplate";
import HelixTemplatesTable from "manage/helix-templates/HelixTemplatesTable";
import BackTopButton from "shared/components/BackTopButton";
import LoadingStar from "shared/components/LoadingStar";

import {
  failedNotification,
  successfulNotification,
  infoNotification,
} from "../../common/utils/notifications";
import { FilterValue, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/es/table/interface";
import {
  EditingState,
  HelixTemplate,
  HELIX_TEMPLATES_BASE_PATH,
} from "manage/helix-templates/Helix";
import Breadcrumbs from "stories/bread-crumb/Breadcrumbs";

type HelixTemplateFormValues = {
  helixIdValue: string;
  idValue?: string;
  nameValue: string;
  descValue: string;
};

const initialEditingValue: EditingState = {
  isEditing: false,
  editingRow: null,
  isDeleting: false,
};

const ManageHelixTemplates: React.FC = () => {
  const { coreClient } = useMercuryContext();
  const editFormRef = useRef<FormInstance | null>(null);
  const [form] = Form.useForm();
  const [editingForm] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [visibleDrawer, setVisibleDrawer] = useState<boolean>(false);
  const [visibleEditDrawer, setVisibleEditDrawer] = useState<boolean>(false);
  const [helixTemplates, setHelixTemplates] = useState<HelixTemplate[]>([]);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<HelixTemplate>>({});
  const [editing, setEditing] = useState<EditingState>(initialEditingValue);
  const [total, setTotal] = useState<number>(0);

  const templatesEndpoint = useMemo(
    () => `${HELIX_TEMPLATES_BASE_PATH}?limit=5000&skip=0`,
    []
  );

  const fetchHelixTemplatesList = async (
    endpoint: string,
    useIsLoading = true
  ) => {
    try {
      setIsLoading(useIsLoading);
      const result = await coreClient.get(`${endpoint}&_=${Date.now()}`);
      if (result?.data) {
        setHelixTemplates(result.data);
        setTotal(result.data.length);
      } else {
        setHelixTemplates([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching helix templates list: ", error);
    }
    setIsLoading(false);
  };

  const addHelixTemplate = async (body: Partial<HelixTemplate>) => {
    const endpoint = HELIX_TEMPLATES_BASE_PATH;
    try {
      setEditing({ ...editing, isEditing: true, isDeleting: false });
      const result = await coreClient.post(endpoint, body);
      if (result.data.error) {
        failedNotification(result.data.error, result.data.message);
      } else {
        successfulNotification(
          `${result.data.name} was added.`,
          "The page will be reloaded. Please refresh your page if the update is not visible."
        );
      }
    } catch (error) {
      console.error("Error adding helix template: ", error);
    }
    setEditing(initialEditingValue);
    handleRefresh();
  };

  const updateHelixTemplate = async (newData: Partial<HelixTemplate>) => {
    if (!editing.editingRow?.id) return;

    const endpoint = `${HELIX_TEMPLATES_BASE_PATH}/${editing.editingRow.id}`;
    try {
      setEditing({ ...editing, isEditing: true, isDeleting: false });
      const result = await coreClient.patch(endpoint, newData);
      successfulNotification(
        `${result.data.name} was updated.`,
        `The page will be reloaded. Please refresh your page if the update is not visible.`
      );
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        failedNotification(error.response?.data?.error, error.response?.data?.detail);
      } else {
        console.error('error', error);
      }
    }

    setEditing(initialEditingValue);
    handleRefresh();
  };

  useEffect(() => {
    fetchHelixTemplatesList(templatesEndpoint);
  }, [templatesEndpoint]);

  const defaultValues = {
    idValue: editing.editingRow?.id,
    helixIdValue: editing.editingRow?.helixId,
    nameValue: editing.editingRow?.name,
    descValue: editing.editingRow?.description,
  };

  useEffect(() => {
    if (editFormRef.current) {
      editingForm.setFieldsValue(defaultValues);
    }
  }, [editFormRef, editingForm, defaultValues]);

  const handleRefresh = async () => {
    try {
      await fetchHelixTemplatesList(templatesEndpoint, false);
      infoNotification(
        "Helix template data reloaded",
        "The Helix template data has been reloaded"
      );
    } catch {
      failedNotification(
        "Failed to reload",
        "Unable to reload the Helix template data at this time, please try again. If the issue persists please contact support."
      );
    }
  };

  const showDrawer = () => setVisibleDrawer(true);
  const closeDrawer = () => {
    setVisibleDrawer(false);
    form.resetFields();
  };

  const onFinishNewTemplate = (values: HelixTemplateFormValues) => {
    const newValues = {
      helixId: values.helixIdValue,
      name: values.nameValue,
      description: values.descValue,
    };
    addHelixTemplate(newValues);
    closeDrawer();
  };

  const showEditDrawer = () => setVisibleEditDrawer(true);

  const closeEditDrawer = (event?: unknown) => {
    setVisibleEditDrawer(false);
    form.resetFields();
    if (event !== undefined) {
      setEditing(initialEditingValue);
    }
  };

  const onFinishEditing = (values: HelixTemplateFormValues) => {
    const { idValue, helixIdValue, nameValue, descValue } = values;
    const newData = {
      id: idValue,
      helixId: helixIdValue,
      name: nameValue,
      description: descValue,
    };
    updateHelixTemplate(newData);
    closeEditDrawer();
  };

  const handleFinishDelete = async (submitted: boolean) => {
    const id = editing.editingRow?.id;
    if (!id) return;

    const endpoint = `${HELIX_TEMPLATES_BASE_PATH}/${id}`;
    try {
      if (!submitted) {
        setEditing(initialEditingValue);
        return;
      }
      setEditing({ ...editing, isEditing: true, isDeleting: true });
      await coreClient.delete(endpoint);
      successfulNotification(
        `The Helix template was deleted.`,
        `The page will be reloaded. Please refresh your page if the update is not visible.`
      );
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        failedNotification(error.response?.data?.error, error.response?.data?.detail);
      } else {
        console.error('error', error);
      }
    }
    setEditing(initialEditingValue);
    handleRefresh();
  };

  const handleChangeTable = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<HelixTemplate> | SorterResult<HelixTemplate>[],
    extra: TableCurrentDataSource<HelixTemplate>
  ) => {
    const actualSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    setSortedInfo(actualSorter);
  };

  const handleClickEdit = (record: HelixTemplate) => {
    setEditing({
      ...editing,
      isEditing: true,
      editingRow: record,
      isDeleting: false,
    });
    showEditDrawer();
  };

  const handleClickDelete = (record: HelixTemplate) => {
    setEditing({
      ...editing,
      isEditing: true,
      editingRow: record,
      isDeleting: true,
    });
  };

  return (
    <div className="manage-page manage-helix-templates-page" style={{ padding: "0 10px" }}>
      <Helmet>
        <title>Manage Helix Templates - Mercury © RWS</title>
      </Helmet>
      {isLoading ? (
        <LoadingStar />
      ) : (
        <>
          <Breadcrumbs
            items={[
              {
                key: "manage-helix-templates",
                label: "Manage Helix Templates"
              }
            ]}
          />
          <AddHelixTemplate
            handlers={{
              showDrawer,
              closeDrawer,
              onFinishNewTemplate,
              handleRefresh,
            }}
            values={{ form, visibleDrawer, helixTemplates }}
          />
          <EditHelixTemplate
            handlers={{
              closeEditDrawer,
              onFinishEditing,
            }}
            values={{
              editingForm,
              visibleEditDrawer,
              editing,
              editFormRef,
              helixTemplates,
            }}
          />
          <DeleteHelixTemplate
            visible={editing.isDeleting}
            onSubmit={handleFinishDelete}
            selectedTemplate={editing.editingRow}
          />
          <HelixTemplatesTable
            values={{ helixTemplates, sortedInfo, editing, total }}
            handlers={{
              handleChangeTable,
              handleClickEdit,
              handleClickDelete,
              handleFinishDelete,
            }}
          />
          <BackTopButton />
        </>
      )}
    </div>
  );
};

export default ManageHelixTemplates;
